import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar, Box, CssBaseline, Drawer, IconButton,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Typography, Avatar, Tooltip, Divider,
} from "@mui/material";
import MenuIcon          from "@mui/icons-material/Menu";
import PeopleIcon        from "@mui/icons-material/People";
import PersonAddIcon     from "@mui/icons-material/PersonAdd";
import LogoutIcon        from "@mui/icons-material/Logout";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

import { sidebarWidth } from "../../config/constants";

const NAV_ITEMS = [
  { label: "Pacientes",       path: "/patients",     icon: <PeopleIcon /> },
  { label: "Nuevo paciente",  path: "/patients/new", icon: <PersonAddIcon /> },
];

export default function Layout() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [open, setOpen] = useState(true);

  const stored = localStorage.getItem("user");
  const user   = stored && stored !== "undefined" ? JSON.parse(stored) : {};

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo / título */}
      <Box sx={{ px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
        <LocalHospitalIcon color="primary" />
        <Typography variant="h6" fontWeight={700} color="primary">
          Rehaday
        </Typography>
      </Box>
      <Divider />

      {/* Navegación */}
      <List sx={{ flex: 1, pt: 1 }}>
        {NAV_ITEMS.map(({ label, path, icon }) => (
          <ListItem key={path} disablePadding>
            <ListItemButton
              selected={location.pathname === path}
              onClick={() => navigate(path)}
              sx={{
                mx: 1, borderRadius: 2,
                "&.Mui-selected": {
                  bgcolor: "primary.main",
                  color:   "white",
                  "& .MuiListItemIcon-root": { color: "white" },
                  "&:hover": { bgcolor: "primary.dark" },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
              <ListItemText primary={label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Usuario + logout */}
      <Divider />
      <Box sx={{ px: 2, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.main", fontSize: 14 }}>
          {user.first_name?.[0]}{user.last_name?.[0]}
        </Avatar>
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {user.first_name} {user.last_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user.hospital_name}
          </Typography>
        </Box>
        <Tooltip title="Cerrar sesión">
          <IconButton size="small" onClick={handleLogout}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* AppBar (mobile) */}
      <AppBar
        position="fixed"
        sx={{
          display: { sm: "none" },
          width: "100%",
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton color="inherit" onClick={() => setOpen(!open)} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={700}>Rehaday</Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          width: sidebarWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: sidebarWidth,
            boxSizing: "border-box",
            border: "none",
            boxShadow: "2px 0 8px rgba(0,0,0,0.08)",
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: "100vh",
          bgcolor: "background.default",
          p: { xs: 2, sm: 3 },
          ml: { sm: 0 },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
